// NumberUtils.test.ts

import { formatNumber } from "react-native-currency-input"

export function roundToDecimal(
  value: number,
  decimalPlaces: number = 1
): number {
  const factor = Math.pow(10, decimalPlaces)
  return Math.round(value * factor) / factor
}

export function numberToCurrency(
  value: number | string,
  decimalPlaces: number = 0
): string {
  const numValue = Number(value)
  if (isNaN(numValue)) {
    return value as string
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimalPlaces,
  }).format(numValue)
}

export function numberToPercentage(
  value: number | string,
  decimalPlaces: number = 2
): string {
  const numValue = Number(value)
  if (isNaN(numValue)) {
    return ""
  }

  return `${numValue.toFixed(decimalPlaces)}%`
}

export function complicatedNumberFormating(value: number): string {
  if (value < 1000 || (value < 100000 && value % 1000 !== 0)) {
    return numberWithCommas(value)
  } else if (value < 1000000) {
    return `${Math.floor(value / 1000)}k`
  } else {
    return `${Math.floor(value / 1000000)}m`
  }
}

export function numberWithCommas(x?: number | string | null): string {
  const num = typeof x === "number" ? x : Number(x)
  if (isNaN(num)) {
    return "0"
  }

  return num.toLocaleString("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })
}

export function stringToNumber(value: string): number {
  const num = Number(value.replace(/[^0-9.-]+/g, ""))
  return isNaN(num) ? 0 : num // Return 0 for non-numeric inputs
}

export function todaysDateAddYears(years: number): Date {
  return new Date(new Date().setFullYear(new Date().getFullYear() + years))
}

export function formatShortDate(dateString: string): string | null {
  const date = new Date(dateString)

  // Check if the date is an Invalid Date
  if (isNaN(date.getTime())) {
    return null
  }

  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  })
}

export function distance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  unit: "KILOMETERS" | "NAUTICAL_MILES" | "MILES"
): number {
  if (lat1 < -90 || lat1 > 90 || lat2 < -90 || lat2 > 90) {
    console.error("Latitude must be between -90 and 90 degrees.")
    return 0
  }
  if (lon1 < -180 || lon1 > 180 || lon2 < -180 || lon2 > 180) {
    console.error("Longitude must be between -180 and 180 degrees.")
    return 0
  }
  if (lat1 === lat2 && lon1 === lon2) {
    return 0
  }
  const radlat1 = (Math.PI * lat1) / 180
  const radlat2 = (Math.PI * lat2) / 180
  const theta = lon1 - lon2
  const radtheta = (Math.PI * theta) / 180
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
  if (dist > 1) {
    dist = 1
  }
  dist = Math.acos(dist)
  dist = (dist * 180) / Math.PI
  dist = dist * 60 * 1.1515
  if (unit === "KILOMETERS") {
    dist = dist * 1.609344
  }
  if (unit === "NAUTICAL_MILES") {
    dist = dist * 0.8684
  }
  // Miles just fall through as dist is by default miles
  return dist
}

export const parseCoordinate = (
  value: string | number | null | undefined
): number | null => {
  if (typeof value === "string") {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? null : parsed
  }
  return value === undefined ? null : value
}

export const abbreviate = (val: number): string => {
  const num = Math.abs(val)
  const thousand = 1000
  const million = 1000000
  const billion = 1000000000

  if (num < thousand) {
    return `${num}`
  } else if (num >= thousand && num < million) {
    if (num === million - 1) {
      return "1M" // Directly return "1M" for 999999
    }
    return `${Math.round(num / thousand)}k`
  } else if (num >= million && num < billion) {
    // Special handling to avoid incorrect rounding for numbers just under a billion
    if (num >= billion - thousand) {
      return "1B" // Directly return "1B" for values just under a billion
    }
    const roundedMillions = Math.round((num / million) * 10) / 10
    return roundedMillions % 1 === 0
      ? `${roundedMillions.toFixed(0)}M`
      : `${roundedMillions}M`
  } else {
    // Handling billions, rounding appropriately
    const roundedBillions = Math.round((num / billion) * 10) / 10
    return roundedBillions % 1 === 0
      ? `${roundedBillions.toFixed(0)}B`
      : `${roundedBillions}B`
  }
}

export const abbreviateCurrency = (val?: number | null): string | null => {
  if (val === undefined || val === null) {
    return null
  }
  const neg = val < 0
  const abbrev = abbreviate(val)
  return `${neg ? "-" : ""}$${abbrev}`
}

export function rangeToCurrencyRange(range: string): string {
  // Check if the input ends with a '+', indicating a format like "100+"
  if (range.trim().endsWith("+")) {
    const numberPart = range.slice(0, -1).trim() // Remove the '+' and trim whitespace
    const num = parseInt(numberPart, 10)
    if (isNaN(num)) {
      console.error("Invalid number in range.")
      return range // Return the original input if it's not a valid number
    }
    // Multiply by 1000 for proper formatting and append '+'
    return `${abbreviateCurrency(num * 1000)}+`
  }

  // Handle the normal range case, such as "0-25"
  const parts = range.split("-")
  if (parts.length === 2) {
    const formattedParts = parts.map((part) => {
      const num = parseInt(part.trim(), 10)
      if (isNaN(num)) {
        console.error("Invalid number in range.")
        return part // Return the original part if it's not a number
      }
      // Special handling for zero as it should not be converted to "$0k"
      if (num === 0) {
        return "0"
      }
      // Multiply other numbers by 1000 to format correctly
      return abbreviateCurrency(num * 1000)
    })
    return formattedParts.join("-") // Join the parts with a dash
  }

  // If the format is neither a valid range nor a "100+" style number
  console.error("Invalid range format. Expected format '25-50' or '100+'.")
  return range // Return the original string if the format is not correct
}

export function convertTimeToDays(option: string): number {
  const timeUnit = option.slice(-1) // Get the last character (m or y)
  const timeValue = parseFloat(option.slice(0, -1)) // Get the numeric part

  switch (timeUnit) {
    case "m":
      return Math.round(timeValue * 31) // Approximate a month as 31 days
    case "y":
      return Math.round(timeValue * 365) // Approximate a year as 365 days
    default:
      return 0 // Default case if the input is not recognized
  }
}

export const displayNumber = ({
  value,
  suffix,
  prefix,
  precision,
}: {
  value: number | null | undefined
  prefix?: string
  suffix?: string
  precision?: number
}) =>
  formatNumber(value || 0, {
    precision: precision || undefined,
    delimiter: ",",
    separator: ".",
    suffix,
    prefix,
  })

export const displayCurrency = ({
  value,
}: {
  value: number | null | undefined
}) => {
  const isNegative = value && value < 0
  const renderValue = Math.abs(value || 0)
  const renderPrefix = isNegative ? "-$" : "$"
  return displayNumber({ value: renderValue, prefix: renderPrefix })
}

/* Examples of usage
  console.log(convertTimeToDays("1m"));  // 30 days
  console.log(convertTimeToDays("6m"));  // 180 days
  console.log(convertTimeToDays("1y"));  // 365 days
  console.log(convertTimeToDays("1.5y")); // 547.5 days, rounded to 548
  console.log(convertTimeToDays("2y"));  // 730 days
*/
